// client/src/dnd/emitter_tool/engine/engine_parts/glow/index.js
// Lightweight glow helpers used by GlowManager (and GravityManager).
// No PIXI dependency.

// ---------- utils ----------
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const clamp01 = (v) => {
  const n = Number(v);
  return clamp(Number.isFinite(n) ? n : 0, 0, 1);
};
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const toBool = (v, d = false) => {
  // Accept booleans, numbers, and common stringy forms.
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
    if (s === "false" || s === "0" || s === "no" || s === "off") return false;
    const n = Number(v);
    if (Number.isFinite(n)) return n !== 0;
  }
  return d;
};

// Simple deterministic hash → [0,1)
function _rand01From(id, salt = 0) {
  let t = (Number(id) | 0) ^ (Number(salt) | 0);
  t ^= t << 13; t ^= t >>> 17; t ^= t << 5;
  return ((t >>> 0) % 1000003) / 1000003;
}

// ---------- API ----------
export function shouldParticleGlow(id, percent01, salt = 0) {
  const r = _rand01From(id, salt);
  return r < clamp01(percent01);
}

/**
 * Map UI settings → runtime params. Supports:
 *  - Flat keys from GlowPanel: glowEnabled, glowPercent (0..100), glowIntensity (0..1),
 *    glowReachM, glowReachPx, glowDurationS
 *  - Legacy/nested: { glow: { enabled, percent01, intensity, reachM, reachPx, durationS } }
 *  - Also tolerates stringy values ("1", "true", etc.)
 */
export function computeGlowParams(effectSettings = {}, pxPerMeter = 40, gridConfig = {}) {
  const es = effectSettings || {};
  const g  = (es && typeof es.glow === "object") ? es.glow : {};

  // ----- enabled (make flat checkbox authoritative when present) -------------
  let enabled;
  if ("glowEnabled" in es) {
    enabled = toBool(es.glowEnabled, false);
  } else if ("enabled" in es) {
    enabled = toBool(es.enabled, false);
  } else if ("enabled" in g) {
    enabled = toBool(g.enabled, false);
  } else if ("glow" in es && typeof es.glow !== "object") {
    enabled = toBool(es.glow, false);
  } else {
    enabled = false;
  }

  // ----- percent01 -----------------------------------------------------------
  // Prefer explicit percent01 (flat or nested). Else derive from 0..100 inputs.
  let percent01;
  if ("percent01" in es) {
    percent01 = clamp01(es.percent01);
  } else if ("percent01" in g) {
    percent01 = clamp01(g.percent01);
  } else if ("glowPercent" in es) {
    percent01 = clamp01(toNum(es.glowPercent, 100) / 100);
  } else if ("percent" in g) {
    // allow nested 'percent' as 0..1
    percent01 = clamp01(toNum(g.percent, 1));
  } else {
    percent01 = 1; // default to 100% selection if not specified
  }

  // ----- intensity -----------------------------------------------------------
  let intensity;
  if ("glowIntensity" in es) intensity = clamp01(es.glowIntensity);
  else if ("intensity" in es) intensity = clamp01(es.intensity);
  else if ("intensity" in g) intensity = clamp01(g.intensity);
  else intensity = 1;

  // ----- reach (metres/pixels) -----------------------------------------------
  // Accept both flat and nested; derive px from metres when needed.
  let reachM;
  if ("glowReachM" in es) reachM = Math.max(0, toNum(es.glowReachM, 0));
  else if ("reachM" in es) reachM = Math.max(0, toNum(es.reachM, 0));
  else if ("reachM" in g) reachM = Math.max(0, toNum(g.reachM, 0));
  else if ("radiusM" in es) reachM = Math.max(0, toNum(es.radiusM, 0));
  else if ("radiusM" in g) reachM = Math.max(0, toNum(g.radiusM, 0));
  else reachM = 0;

  let reachPx;
  if ("glowReachPx" in es) reachPx = Math.max(0, toNum(es.glowReachPx, 0));
  else if ("reachPx" in es) reachPx = Math.max(0, toNum(es.reachPx, 0));
  else if ("reachPx" in g) reachPx = Math.max(0, toNum(g.reachPx, 0));
  else if ("radiusPx" in es) reachPx = Math.max(0, toNum(es.radiusPx, 0));
  else if ("radiusPx" in g) reachPx = Math.max(0, toNum(g.radiusPx, 0));
  else reachPx = 0;

  // pxPerMeter fallbacks
  let ppm = 0;
  if (Number.isFinite(pxPerMeter) && pxPerMeter > 0) ppm = pxPerMeter;
  else if (Number.isFinite(gridConfig.pxPerMeter) && gridConfig.pxPerMeter > 0) ppm = gridConfig.pxPerMeter;
  else if (Number.isFinite(gridConfig.metre_in_pixels) && gridConfig.metre_in_pixels > 0) ppm = gridConfig.metre_in_pixels;
  else if (Number.isFinite(gridConfig.meter_in_pixels) && gridConfig.meter_in_pixels > 0) ppm = gridConfig.meter_in_pixels;

  if (!reachPx && reachM > 0 && ppm > 0) {
    reachPx = reachM * ppm;
  }

  // ----- duration ------------------------------------------------------------
  let durationS;
  if ("glowDurationS" in es) durationS = Math.max(0, toNum(es.glowDurationS, 0.75));
  else if ("durationS" in es) durationS = Math.max(0, toNum(es.durationS, 0.75));
  else if ("durationS" in g) durationS = Math.max(0, toNum(g.durationS, 0.75));
  else durationS = 0.75;

  return { enabled, percent01, intensity, reachM, reachPx, durationS };
}

export function computePerParticleCooldownS(intensity01 = 1) {
  const hi = clamp01(intensity01);
  const minS = 0.06, maxS = 0.20;
  return clamp(minS + (1 - hi) * (maxS - minS), minS, maxS);
}

export function dispatchGlowPulse(payload = {}, eventName = "fx-glow") {
  try {
    const detail = { ...payload };
    if (detail.x != null) detail.x = +detail.x;
    if (detail.y != null) detail.y = +detail.y;
    if (detail.radiusM != null) detail.radiusM = Math.max(0, +detail.radiusM);
    if (detail.pxPerMeter != null) detail.pxPerMeter = Math.max(0, +detail.pxPerMeter);
    if (detail.radiusPx != null) detail.radiusPx = Math.max(0, +detail.radiusPx);
    if (detail.alpha != null) detail.alpha = clamp01(+detail.alpha);
    if (detail.durationS != null) detail.durationS = Math.max(0, +detail.durationS);

    window.dispatchEvent(new CustomEvent(eventName || "fx-glow", { detail }));
  } catch {
    // ignore (SSR, CSP)
  }
}

export default {
  shouldParticleGlow,
  computeGlowParams,
  computePerParticleCooldownS,
  dispatchGlowPulse,
};
